import React, { useState, useEffect } from 'react';
import { 
  Grid, Box, Typography, Card, CardContent,
  Divider, Paper, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Button, Collapse, IconButton, FormControlLabel, Checkbox,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  FormHelperText
} from '@material-ui/core';
import { makeStyles } from "@material-ui/core/styles";
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import InfoIcon from '@material-ui/icons/Info';
import Tooltip from '@material-ui/core/Tooltip';
import PersonIcon from '@material-ui/icons/Person';
import ChildCareIcon from '@material-ui/icons/ChildCare';
import SchoolIcon from '@material-ui/icons/School';
import { v4 as uuidv4 } from 'uuid';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    maxWidth: '900px',
    margin: '0 auto',
  },
  premiumSummary: {
    marginBottom: theme.spacing(3),
    backgroundColor: '#ffffff',
  },
  summaryTitle: {
    fontWeight: 600,
    color: '#032A37',
  },
  memberCard: {
    marginBottom: theme.spacing(3),
  },
  mainMember: {
    backgroundColor: 'rgba(251, 169, 45, 0.1)',
  },
  addButton: {
    marginTop: theme.spacing(2),
    backgroundColor: '#032A37',
    color: '#ffffff',
    '&:hover': {
      backgroundColor: '#043e4f',
    },
  },
  deleteButton: {
    color: theme.palette.error.main,
  },
  benefitsList: {
    backgroundColor: '#f9fafb',
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
  },
  dialogContent: {
    padding: theme.spacing(2),
  },
  dialogTitle: {
    backgroundColor: '#032A37',
    color: '#ffffff',
  },
  dialogActions: {
    padding: theme.spacing(2),
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: '0.75rem',
    marginTop: theme.spacing(0.5),
    marginLeft: theme.spacing(1.5),
  },
  typeSelectionDialog: {
    padding: theme.spacing(3),
  },
  memberTypeContainer: {
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    marginTop: theme.spacing(2),
  },
  memberTypeButton: {
    margin: theme.spacing(1),
    padding: theme.spacing(2, 3),
    borderRadius: theme.spacing(1),
    backgroundColor: '#f5f5f5',
    border: '1px solid #e0e0e0',
    '&:hover': {
      backgroundColor: '#eeeeee',
    },
    width: '45%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  memberTypeButtonSelected: {
    backgroundColor: 'rgba(251, 169, 45, 0.1)',
    border: '1px solid #FBA92D',
    '&:hover': {
      backgroundColor: 'rgba(251, 169, 45, 0.15)',
    },
  },
  memberTypeIcon: {
    fontSize: '2rem',
    marginBottom: theme.spacing(1),
  },
  disabledButton: {
    opacity: 0.5,
    backgroundColor: '#f0f0f0',
    cursor: 'not-allowed',
    '&:hover': {
      backgroundColor: '#f0f0f0',
    },
  },
  inSchoolContainer: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  }
}));

// Age validation constants
const SPOUSE_MIN_AGE = 18;
const SPOUSE_MAX_AGE = 65;
const CHILD_MIN_AGE_DAYS = 30;
const CHILD_MAX_AGE = 18;
const CHILD_MAX_AGE_IN_SCHOOL = 25;

// Age validation utilities
const calculateAge = (birthDate) => {
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  
  return age;
};

const calculateDaysOld = (birthDate) => {
  const today = new Date();
  const birth = new Date(birthDate);
  const diffTime = Math.abs(today - birth);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

const validateSpouseDOB = (dob) => {
  if (!dob) return { valid: false, message: "Date of birth is required" };
  
  const age = calculateAge(dob);
  
  if (age < SPOUSE_MIN_AGE) {
    return { valid: false, message: `Spouse must be at least ${SPOUSE_MIN_AGE} years old` };
  }
  
  if (age > SPOUSE_MAX_AGE) {
    return { valid: false, message: `Spouse must be under ${SPOUSE_MAX_AGE} years old` };
  }
  
  return { valid: true, message: "" };
};

const validateChildDOB = (dob, isInSchool) => {
  if (!dob) return { valid: false, message: "Date of birth is required" };
  
  const age = calculateAge(dob);
  const daysOld = calculateDaysOld(dob);
  
  if (daysOld < CHILD_MIN_AGE_DAYS) {
    return { valid: false, message: `Child must be at least ${CHILD_MIN_AGE_DAYS} days old` };
  }
  
  const maxAge = isInSchool ? CHILD_MAX_AGE_IN_SCHOOL : CHILD_MAX_AGE;
  
  if (age > maxAge) {
    return { 
      valid: false, 
      message: isInSchool 
        ? `Child must be under ${CHILD_MAX_AGE_IN_SCHOOL} years old` 
        : `Child must be under ${CHILD_MAX_AGE} years old` 
    };
  }
  
  return { valid: true, message: "" };
};

const benefits = [
  {
    "name": "Malkia Cancer Cash Benefit",
    "limit": "KES 200,000"
  },
  {
    "name": "Last Expense",
    "limit": "KES 50,000"
  },
  {
    "name": "Daily Hospital Cash",
    "limit": "KES 1,000"
  }
];

const PersonsCovered = ({ setMembers, members, goBack, mainMember, selectedPremium, handleCreateQuote, setCurrentComponent, COMPONENT_TYPES }) => {
  const classes = useStyles();
  
  console.log('mainMember', mainMember);
  
  // Initialize members with main member
  // const [members, setMembers] = useState([]);
  
  // Track expanded rows for accordion
  const [expandedRows, setExpandedRows] = useState({});
  
  // Premium summary
  const [premiumSummary, setPremiumSummary] = useState({
    mainMember: 0,
    spouse: 0,
    children: 0,
    total: 0
  });
  
  // Dialog states
  const [openMemberTypeDialog, setOpenMemberTypeDialog] = useState(false);
  const [openMemberFormDialog, setOpenMemberFormDialog] = useState(false);
  const [memberType, setMemberType] = useState('');
  const [newMember, setNewMember] = useState({
    first_name: '',
    middle_name: '',
    last_name: '',
    date_of_birth: '',
    gender: '',
    national_id: '',
    passport: 'N/A',
    relationship: '',
    isInSchool: false
  });
  
  // Form validation
  const [dobError, setDobError] = useState({ valid: true, message: "" });

  // Max children allowed
  const MAX_CHILDREN = 4;
  
  // Add main member to members on first render
  useEffect(() => {
    if (mainMember && members.length === 0) {
      setMembers([{...mainMember, isMainMember: true}]);
    }
  }, [mainMember]);

  // Update premium calculation when members or selected premium changes
  useEffect(() => {
    if (!selectedPremium) return;
    
    let spouseCount = 0;
    let childrenCount = 0;
    
    // Count spouses and children
    members.forEach(member => {
      if (member.relationship === 'Spouse') {
        spouseCount++;
      } else if (member.relationship === 'Child') {
        childrenCount++;
      }
    });
    
    let premiumObj = {
      mainMember: selectedPremium.CHP,
      spouse: spouseCount > 0 ? selectedPremium.SPOUSE : 0,
      children: childrenCount > 0 ? selectedPremium.CHILD * childrenCount : 0,
      total: selectedPremium.CHP
    };
    
    if (spouseCount > 0) {
      premiumObj.total += selectedPremium.SPOUSE;
    }
    
    if (childrenCount > 0) {
      premiumObj.total += selectedPremium.CHILD * childrenCount;
    }
    
    setPremiumSummary(premiumObj);
    
  }, [members, selectedPremium]);

  // Toggle row expansion
  const handleRowExpand = (index) => {
    setExpandedRows(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Handle opening member type dialog
  const handleOpenMemberTypeDialog = () => {
    setOpenMemberTypeDialog(true);
    setMemberType('');
    // Reset form
    setNewMember({
      first_name: '',
      middle_name: '',
      last_name: '',
      date_of_birth: '',
      gender: '',
      national_id: '',
      passport: 'N/A',
      relationship: '',
      isInSchool: false
    });
    setDobError({ valid: true, message: "" });
  };

  // Handle closing member type dialog
  const handleCloseMemberTypeDialog = () => {
    setOpenMemberTypeDialog(false);
  };

  // Handle opening member form dialog
  // const handleOpenMemberFormDialog = () => {
  //   setOpenMemberFormDialog(true);
  // };

  // Handle closing member form dialog
  const handleCloseMemberFormDialog = () => {
    setOpenMemberFormDialog(false);
  };

  // Handle member type selection and proceed to form
  const handleMemberTypeChange = (type) => {
    setMemberType(type);
    // Reset form fields
    setNewMember({
      first_name: '',
      middle_name: '',
      last_name: '',
      date_of_birth: '',
      gender: '',
      national_id: '',
      passport: 'N/A',
      relationship: type === 'spouse' ? 'Spouse' : 'Child',
      isInSchool: false
    });
    setDobError({ valid: true, message: "" });
    // Close type dialog and open form dialog
    setOpenMemberTypeDialog(false);
    setOpenMemberFormDialog(true);
  };

  // Handle input changes in the dialog form
  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    const updatedMember = {
      ...newMember,
      [name]: newValue
    };
    
    setNewMember(updatedMember);
    
    // Validate DOB when it changes
    if (name === 'date_of_birth') {
      if (updatedMember.relationship === 'Spouse') {
        setDobError(validateSpouseDOB(value));
      } else if (updatedMember.relationship === 'Child') {
        setDobError(validateChildDOB(value, updatedMember.isInSchool));
      }
    }
    
    // Revalidate child's DOB if isInSchool changes
    if (name === 'isInSchool' && updatedMember.relationship === 'Child' && updatedMember.date_of_birth) {
      setDobError(validateChildDOB(updatedMember.date_of_birth, newValue));
    }
  };

  // Handle adding a new member
  const handleAddMember = () => {
    if (!newMember.first_name || !newMember.last_name || !newMember.date_of_birth) {
      return; // Simple validation
    }
    
    // Validate DOB before adding
    let dobValidation;
    if (newMember.relationship === 'Spouse') {
      dobValidation = validateSpouseDOB(newMember.date_of_birth);
    } else if (newMember.relationship === 'Child') {
      dobValidation = validateChildDOB(newMember.date_of_birth, newMember.isInSchool);
    }
    
    if (!dobValidation.valid) {
      setDobError(dobValidation);
      return;
    }
    
    // Use the same format as in AddDependant
    const chpMemberBenefit = [
      {
        type: "HOSPITAL ADMISSION",
        cover_amount_in_cents: 30000 * 100,
        waiting_period_in_days: 30,
        cover_usage_count: 30,
        benefit_period_cardinality: 1,
        benefit_period_granuality: "year",
        premium_amount: 0,
        is_initialized: true,
        additional_details: {
          premium_amount: 0,
        }
      },
      {
        type: "CANCER CARE",
        cover_amount_in_cents: 20000000,
        waiting_period_in_days: 30,
        cover_usage_count: 0,
        benefit_period_cardinality: 1,
        benefit_period_granuality: "year",
        premium_amount: 0,
        is_initialized: true,
        additional_details: {
          premium_amount: 0,
        }
      },
      {
        type: "LAST EXPENSE",
        cover_amount_in_cents: 5000000,
        waiting_period_in_days: 30,
        cover_usage_count: 0,
        benefit_period_cardinality: 1,
        benefit_period_granuality: "year",
        premium_amount: 0,
        is_initialized: true,
        additional_details: {
          premium_amount: 0,
        }
      }
    ];
    
    const member_uuid = uuidv4();
    let memberPremium = 0;
    
    if (newMember.relationship === "Spouse") {
      memberPremium = selectedPremium ? selectedPremium.SPOUSE : 0;
    } else if (newMember.relationship === "Child") {
      memberPremium = selectedPremium ? selectedPremium.CHILD : 0;
    }
    
    const payload = {
      "first_name": newMember.first_name,
      "middle_name": newMember.middle_name || "",
      "last_name": newMember.last_name,
      "msisdn": "",
      "date_of_birth": newMember.date_of_birth,
      "gender": newMember.gender || "",
      "national_id": newMember.national_id || "N/A",
      "passport": newMember.passport || "N/A",
      "relationship": newMember.relationship,
      "residential_address": "",
      "premium_amount_in_cents": memberPremium * 100,
      "benefits": [...chpMemberBenefit],
      "allbenefits": [...chpMemberBenefit],
      "uuid": member_uuid,
      "additional_details": {
        "uuid": member_uuid,
      },
      "isInSchool": newMember.isInSchool
    };
    
    console.log('Adding new member:', payload);
    
    // Create member object for UI display
    const fullname = `${newMember.first_name} ${newMember.last_name}`;
    const memberId = `${newMember.relationship.toLowerCase()}-${Date.now()}`;
    
    const memberToAdd = {
      ...newMember,
      id: memberId,
      fullname,
      isMainMember: false,
      benefits: benefits,
      uuid: member_uuid
    };
    
    setMembers(prev => [...prev, memberToAdd]);
    setOpenMemberFormDialog(false);
  };

  // Handle removing a member
  const handleRemoveMember = (index) => {
    setMembers(prev => prev.filter((_, i) => i !== index));
  };

  // Format premium amount with thousand separators
  const formatPremium = (amount) => {
    return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // Check if spouse is already added
  const hasSpouse = members.some(member => member.relationship === 'Spouse');
  
  // Count existing children
  const childrenCount = members.filter(member => member.relationship === 'Child').length;
  
  // Check if max children reached
  const hasMaxChildren = childrenCount >= MAX_CHILDREN;

  return (
    <div className={classes.root}>
      {/* Premium Summary Card */}
      <Card className={classes.premiumSummary} elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="center" marginBottom={1}>
            <Typography variant="h5" className={classes.summaryTitle}>
              Premium Summary - {selectedPremium ? selectedPremium.frequency : 'Loading...'}
            </Typography>
          </Box>
          <Divider style={{ margin: '12px 0', backgroundColor: '#FBA92D', height: '2px', opacity: 0.7 }} />
          
          {!selectedPremium ? (
            <Box textAlign="center" my={4}>
              <Typography variant="body1">
                Please select a premium plan to continue.
              </Typography>
            </Box>
          ) : (
            <>
              <Box mt={2} mb={1}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                      <Typography variant="body2" style={{ color: '#666', marginBottom: '4px' }}>
                        Main Member (CHP)
                      </Typography>
                      <Typography variant="h6" style={{ fontWeight: '600', color: '#032A37' }}>
                        Ksh {formatPremium(premiumSummary.mainMember)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                      <Typography variant="body2" style={{ color: '#666', marginBottom: '4px' }}>
                        Spouse {premiumSummary.spouse > 0 ? '' : '(Not Included)'}
                      </Typography>
                      <Typography variant="h6" style={{ fontWeight: '600', color: premiumSummary.spouse > 0 ? '#032A37' : '#999' }}>
                        Ksh {formatPremium(premiumSummary.spouse)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                      <Typography variant="body2" style={{ color: '#666', marginBottom: '4px' }}>
                        Children ({members.filter(m => m.relationship === 'Child').length})
                      </Typography>
                      <Typography variant="h6" style={{ fontWeight: '600', color: premiumSummary.children > 0 ? '#032A37' : '#999' }}>
                        Ksh {formatPremium(premiumSummary.children)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
              
              <Divider style={{ margin: '16px 0', backgroundColor: '#eaeaea' }} />
              
              <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
                <Paper elevation={0} style={{ 
                  backgroundColor: 'rgba(251, 169, 45, 0.15)', 
                  padding: '12px 24px', 
                  borderRadius: '8px', 
                  display: 'inline-block' 
                }}>
                  <Typography variant="h5" style={{ fontWeight: '700', color: '#032A37' }}>
                    Total Premium: Ksh {formatPremium(premiumSummary.total)}
                  </Typography>
                </Paper>
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* Members List Card */}
      <Card className={classes.memberCard}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={2}>
            <Typography variant="h6">Members</Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<PersonAddIcon />}
              onClick={handleOpenMemberTypeDialog}
              className={classes.addButton}
              disabled={hasSpouse && hasMaxChildren}
            >
              Add Member
            </Button>
          </Box>
          
          <TableContainer component={Paper}>
            <Table size="medium">
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: '40px' }}></TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Relationship</TableCell>
                  <TableCell>Premium</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {members.map((member, index) => {
                  const isExpanded = expandedRows[index];
                  let memberPremium = 0;
                  
                  if (member.relationship === "Principal") {
                    memberPremium = selectedPremium ? selectedPremium.CHP : 0;
                  } else if (member.relationship === "Spouse") {
                    memberPremium = selectedPremium ? selectedPremium.SPOUSE : 0;
                  } else if (member.relationship === "Child") {
                    memberPremium = selectedPremium ? selectedPremium.CHILD : 0;
                  }
                  
                  return (
                    <React.Fragment key={member.id || index}>
                      <TableRow className={member.isMainMember ? classes.mainMember : ''}>
                        <TableCell>
                          <IconButton 
                            aria-label="expand row"
                            size="small"
                            onClick={() => handleRowExpand(index)}
                          >
                            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>{member.first_name + " " + member.last_name}</TableCell>
                        <TableCell>{member.relationship}</TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <Typography>Ksh {formatPremium(memberPremium)}</Typography>
                            <Tooltip title={`Premium for ${member.relationship}`}>
                              <InfoIcon style={{ fontSize: 16, marginLeft: 4, color: '#666' }} />
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {!member.isMainMember && (
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => handleRemoveMember(index)}
                            >
                              <DeleteOutlinedIcon />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box margin={1} padding={2}>
                              <Typography variant="h6" gutterBottom component="div">
                                Member Details
                              </Typography>
                              
                              {/* Personal Information */}
                              <Box mb={3}>
                                <Typography variant="subtitle1" style={{ fontWeight: '600', marginBottom: '8px' }}>
                                  Personal Information
                                </Typography>
                                <Grid container spacing={2}>
                                  <Grid item xs={6} sm={4}>
                                    <Typography variant="body2" color="textSecondary">First Name</Typography>
                                    <Typography variant="body1">{member.first_name || '-'}</Typography>
                                  </Grid>
                                  <Grid item xs={6} sm={4}>
                                    <Typography variant="body2" color="textSecondary">Last Name</Typography>
                                    <Typography variant="body1">{member.last_name || '-'}</Typography>
                                  </Grid>
                                  <Grid item xs={6} sm={4}>
                                    <Typography variant="body2" color="textSecondary">Date of Birth</Typography>
                                    <Typography variant="body1">{member.dob || '-'}</Typography>
                                  </Grid>
                                  <Grid item xs={6} sm={4}>
                                    <Typography variant="body2" color="textSecondary">ID Number</Typography>
                                    <Typography variant="body1">{member.national_id || '-'}</Typography>
                                  </Grid>
                                  <Grid item xs={6} sm={4}>
                                    <Typography variant="body2" color="textSecondary">Relationship</Typography>
                                    <Typography variant="body1">{member.relationship || '-'}</Typography>
                                  </Grid>
                                </Grid>
                              </Box>
                              
                              {/* Benefits */}
                              <Typography variant="subtitle1" style={{ fontWeight: '600', marginBottom: '8px', marginTop: '16px' }}>
                                Benefits
                              </Typography>
                              <Box className={classes.benefitsList}>
                                {member.benefits && member.benefits.length > 0 ? (
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Benefit Name</TableCell>
                                        <TableCell>Limit</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {member.benefits.map((benefit, index) => (
                                        <TableRow key={index}>
                                          <TableCell>{benefit.name}</TableCell>
                                          <TableCell>{benefit.limit}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                ) : (
                                  <Typography>No benefits selected</Typography>
                                )}
                              </Box>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Box mt={4} display="flex" justifyContent="center" width="100%" maxWidth="600px" margin="0 auto">
        <Button
          variant="outlined"
          color="default"
          size="large"
          onClick={() => goBack()}
          style={{
            borderColor: '#032A37',
            color: '#032A37',
            padding: '10px 24px',
            fontSize: '15px',
            fontWeight: 500
          }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleCreateQuote}
          disabled={members.length === 0}
          style={{
            backgroundColor: '#032A37',
            color: '#ffffff',
            padding: '12px 32px',
            fontSize: '16px',
            fontWeight: 500
          }}
        >
          Create Quote
        </Button>
      </Box>

      {/* Member Type Selection Dialog */}
      <Dialog
        open={openMemberTypeDialog}
        onClose={handleCloseMemberTypeDialog}
        aria-labelledby="select-member-type-dialog"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="select-member-type-dialog" className={classes.dialogTitle}>Select Member Type</DialogTitle>
        <DialogContent className={classes.typeSelectionDialog}>
          <Typography variant="body1" gutterBottom>
            Please select the type of family member you would like to add:
          </Typography>
          
          <Box className={classes.memberTypeContainer}>
            <Button
              className={`${classes.memberTypeButton} ${memberType === 'spouse' ? classes.memberTypeButtonSelected : ''} ${hasSpouse ? classes.disabledButton : ''}`}
              onClick={() => !hasSpouse && handleMemberTypeChange('spouse')}
              disabled={hasSpouse}
            >
              <PersonIcon className={classes.memberTypeIcon} />
              <Typography variant="subtitle1">Spouse</Typography>
              {hasSpouse && (
                <Typography variant="caption" color="textSecondary">
                  Already added
                </Typography>
              )}
            </Button>
            
            <Button
              className={`${classes.memberTypeButton} ${memberType === 'child' ? classes.memberTypeButtonSelected : ''} ${hasMaxChildren ? classes.disabledButton : ''}`}
              onClick={() => !hasMaxChildren && handleMemberTypeChange('child')}
              disabled={hasMaxChildren}
            >
              <ChildCareIcon className={classes.memberTypeIcon} />
              <Typography variant="subtitle1">Child</Typography>
              <Typography variant="caption" color="textSecondary">
                {childrenCount}/{MAX_CHILDREN} added
              </Typography>
            </Button>
          </Box>
          
          <Box mt={3}>
            <Typography variant="body2" color="textSecondary">
              {hasSpouse && hasMaxChildren ? (
                "You've reached the maximum number of family members that can be added."
              ) : (
                "Family members will be covered under the selected premium plan."
              )}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={handleCloseMemberTypeDialog} color="secondary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Member Details Form Dialog */}
      <Dialog
        open={openMemberFormDialog}
        onClose={handleCloseMemberFormDialog}
        aria-labelledby="add-member-details-dialog"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="add-member-details-dialog" className={classes.dialogTitle}>
          {memberType === 'spouse' ? 'Add Spouse' : 'Add Child'}
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Box mt={3}>
            <Typography variant="subtitle1" gutterBottom>
              {memberType === 'spouse' ? 'Spouse Details' : 'Child Details'}
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="first_name"
                  label="First Name"
                  value={newMember.first_name}
                  onChange={handleInputChange}
                  variant="outlined"
                  fullWidth
                  className={classes.formField}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="last_name"
                  label="Last Name"
                  value={newMember.last_name}
                  onChange={handleInputChange}
                  variant="outlined"
                  fullWidth
                  className={classes.formField}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="gender"
                  label="Gender"
                  select
                  value={newMember.gender}
                  onChange={handleInputChange}
                  variant="outlined"
                  fullWidth
                  className={classes.formField}
                  required
                >
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="date_of_birth"
                  label="Date of Birth"
                  type="date"
                  value={newMember.date_of_birth}
                  onChange={handleInputChange}
                  variant="outlined"
                  fullWidth
                  className={classes.formField}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  error={!dobError.valid}
                  helperText={!dobError.valid ? dobError.message : memberType === 'spouse' ? 
                    `Age must be between ${SPOUSE_MIN_AGE}-${SPOUSE_MAX_AGE} years` : 
                    `Age must be between ${CHILD_MIN_AGE_DAYS} days and ${newMember.isInSchool ? CHILD_MAX_AGE_IN_SCHOOL : CHILD_MAX_AGE} years`}
                  required
                />
              </Grid>
              
              {memberType === 'child' && (
                <Grid item xs={12} sm={6} className={classes.inSchoolContainer}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="isInSchool"
                        checked={newMember.isInSchool}
                        onChange={handleInputChange}
                        color="primary"
                      />
                    }
                    label={
                      <Box display="flex" alignItems="center">
                        <SchoolIcon style={{ marginRight: 8 }} />
                        <Typography>Child is in school</Typography>
                      </Box>
                    }
                  />
                  <FormHelperText>
                    Children in school are covered up to 25 years
                  </FormHelperText>
                </Grid>
              )}
              
              <Grid item xs={12} sm={6}>
                <TextField
                  name="national_id"
                  label="ID Number"
                  value={newMember.national_id}
                  onChange={handleInputChange}
                  variant="outlined"
                  fullWidth
                  className={classes.formField}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={handleCloseMemberFormDialog} color="secondary">
            Cancel
          </Button>
          <Button 
            onClick={handleAddMember}
            color="primary"
            variant="contained"
            disabled={!newMember.first_name || !newMember.last_name || !newMember.date_of_birth || !dobError.valid}
          >
            Add Member
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PersonsCovered;